export interface AuthUser {
  id: string;
  username: string;
  email: string;
  name: string;
  avatar_url?: string;
  groups?: string[];
}

export interface AuthSession {
  id: string;
  status: 'pending' | 'authenticated';
  user?: AuthUser;
  createdAt: Date;
  authenticatedAt?: Date;
}

export interface AuthStatus {
  authenticated: boolean;
  user: AuthUser | null;
}

class AuthService {
  private sessionId: string | null = null;
  private popupWindow: Window | null = null;
  private checkInterval: NodeJS.Timeout | null = null;
  private baseUrl = process.env.REACT_APP_BACKEND_URL;

  /**
   * Set session ID manually (for external authentication flows)
   */
  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  /**
   * Initialize authentication service
   */
  async init(): Promise<AuthStatus> {
    // Check if we have a stored session
    const storedSessionId = localStorage.getItem('auth_session_id');
    if (storedSessionId) {
      this.sessionId = storedSessionId;
      return this.checkAuthStatus();
    }
    
    return { authenticated: false, user: null };
  }

  /**
   * Start SSO authentication process with popup
   */
  async startSSO(): Promise<void> {
    try {
      console.log('🚀 Starting SSO authentication with popup...');
      
      // Get SSO URL from backend - updated endpoint
      const response = await fetch(`${this.baseUrl}/api/auth/sso`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to get SSO URL');
      }

      console.log('📋 SSO URL received:', data.ssoUrl);
      console.log('🆔 Session ID:', data.sessionId);

      this.sessionId = data.sessionId;
      localStorage.setItem('auth_session_id', this.sessionId!);
      
      // Open popup window DIRECTLY to Discourse forum (not backend)
      console.log('🔗 Opening popup directly to Discourse:', data.ssoUrl);
      this.openSSOPopup(data.ssoUrl);

      // Start listening for popup messages and polling
      this.startAuthListener();

    } catch (error) {
      console.error('Error starting SSO:', error);
      throw error;
    }
  }

  /**
   * Open SSO popup window
   */
  private openSSOPopup(ssoUrl: string): void {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    console.log('🔗 Opening SSO popup:', ssoUrl);

    this.popupWindow = window.open(
      ssoUrl,
      'discourse_sso',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no`
    );

    if (!this.popupWindow) {
      throw new Error('Popup blocked by browser. Please allow popups for this site.');
    }
  }

  /**
   * Start listening for authentication completion
   */
  private startAuthListener(): void {
    console.log('👂 Starting auth listeners for webhook flow...');
    
    // Listen for postMessage from popup (primary method)
    const handleMessage = (event: MessageEvent) => {
      console.log('📨 Received message from:', event.origin);
      console.log('📨 Message data:', event.data);
      
      // Temporarily disable origin check for debugging
      console.log('🔒 Current origin:', event.origin);
      console.log('🔍 Skipping origin check for debugging');
      
      // Check for auth success message (NEW FORMAT)
      if (event.data?.type === 'AUTH_SUCCESS' && event.data?.sessionId) {
        console.log('✅ Auth success via postMessage with session ID:', event.data.sessionId);
        console.log('🔚 Closing popup from parent window');
        
        // Close the popup immediately from parent window
        if (this.popupWindow && !this.popupWindow.closed) {
          this.popupWindow.close();
          this.popupWindow = null;
        }
        
        this.handleAuthSuccess(event.data.sessionId);
        window.removeEventListener('message', handleMessage);
        return;
      }
      
      // Check for auth error message (NEW FORMAT)
      if (event.data?.type === 'AUTH_ERROR') {
        console.log('❌ Auth error via postMessage:', event.data.error);
        
        // Close the popup on error
        if (this.popupWindow && !this.popupWindow.closed) {
          this.popupWindow.close();
          this.popupWindow = null;
        }
        
        this.handleAuthError(event.data.error);
        window.removeEventListener('message', handleMessage);
        return;
      }

      // Legacy support for old message format
      if (event.data?.type === 'DISCOURSE_AUTH_SUCCESS') {
        console.log('✅ Auth success via postMessage (legacy)');
        console.log('🔚 Closing popup from parent window (legacy)');
        
        // Close the popup immediately from parent window
        if (this.popupWindow && !this.popupWindow.closed) {
          this.popupWindow.close();
          this.popupWindow = null;
        }
        
        this.handleAuthSuccess(event.data.sessionId);
        window.removeEventListener('message', handleMessage);
        return;
      }
      
      if (event.data?.type === 'DISCOURSE_AUTH_ERROR') {
        console.log('❌ Auth error via postMessage (legacy):', event.data.error);
        
        // Close the popup on error
        if (this.popupWindow && !this.popupWindow.closed) {
          this.popupWindow.close();
          this.popupWindow = null;
        }
        
        this.handleAuthError(event.data.error);
        window.removeEventListener('message', handleMessage);
        return;
      }
    };

    window.addEventListener('message', handleMessage);

    // Also start polling as backup method for webhook flow
    this.startPolling(handleMessage);
  }

  /**
   * Start polling for authentication completion (backup method for webhook)
   */
  private startPolling(messageHandler: (event: MessageEvent) => void): void {
    console.log('🔄 Starting polling backup for webhook authentication...');
    
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(async () => {
      try {
        // Check if popup is still open
        if (this.popupWindow && this.popupWindow.closed) {
          console.log('🔚 Popup closed by user');
          this.stopPolling();
          window.removeEventListener('message', messageHandler);
          window.dispatchEvent(new CustomEvent('auth:cancelled'));
          return;
        }

        // Check if there's a new session in localStorage (updated by callback)
        const newSessionId = localStorage.getItem('auth_session_id');
        if (newSessionId && newSessionId !== this.sessionId) {
          console.log('🔄 Polling: Found new session ID in localStorage:', newSessionId);
          this.sessionId = newSessionId;
        }
        
        // Check auth status with current/new session ID
        const status = await this.checkAuthStatus();
        
        if (status.authenticated && status.user) {
          console.log('✅ Auth success via polling with session:', this.sessionId);
          this.handleAuthSuccess(this.sessionId!);
          window.removeEventListener('message', messageHandler);
        }
      } catch (error) {
        console.error('Error during polling:', error);
      }
    }, 2000); // Check every 2 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      console.log('⏰ Auth timeout');
      this.stopPolling();
      this.closePopup();
      window.removeEventListener('message', messageHandler);
      window.dispatchEvent(new CustomEvent('auth:timeout'));
    }, 5 * 60 * 1000);
  }

  /**
   * Handle successful authentication
   */
  private async handleAuthSuccess(sessionId: string): Promise<void> {
    try {
      console.log('🔄 AuthService: Updating session ID from', this.sessionId, 'to', sessionId);
      this.sessionId = sessionId;
      localStorage.setItem('auth_session_id', sessionId);
      
      // Get user data
      const status = await this.checkAuthStatus();
      
      this.stopPolling();
      this.closePopup();
      
      // Trigger success event with user data
      window.dispatchEvent(new CustomEvent('auth:success', { 
        detail: { user: status.user, sessionId } 
      }));
      
      console.log('🎉 Authentication completed successfully via webhook');
    } catch (error) {
      console.error('Error handling auth success:', error);
      this.handleAuthError('Failed to get user data');
    }
  }

  /**
   * Handle authentication error
   */
  private handleAuthError(error: string): void {
    this.stopPolling();
    this.closePopup();
    
    window.dispatchEvent(new CustomEvent('auth:error', { 
      detail: { error } 
    }));
    
    console.log('❌ Authentication failed:', error);
  }

  /**
   * Stop polling for authentication
   */
  private stopPolling(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Close SSO popup window
   */
  private closePopup(): void {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
      this.popupWindow = null;
    }
  }

  /**
   * Check authentication status
   */
  async checkAuthStatus(): Promise<AuthStatus> {
    console.log('🔍 AuthService: Checking auth status for session:', this.sessionId);
    
    if (!this.sessionId) {
      console.log('❌ AuthService: No session ID available');
      return { authenticated: false, user: null };
    }

    try {
      console.log('🌐 AuthService: Making request to:', `${this.baseUrl}/api/auth/status`);
      console.log('📋 AuthService: Headers:', { 'X-Session-ID': this.sessionId });
      
      const response = await fetch(`${this.baseUrl}/api/auth/status`, {
        headers: {
          'X-Session-ID': this.sessionId
        }
      });

      const data = await response.json();
      console.log('📊 AuthService: Response data:', data);
      
      // If not authenticated, clear stored session
      if (!data.authenticated) {
        console.log('❌ AuthService: Not authenticated, clearing session');
        localStorage.removeItem('auth_session_id');
        this.sessionId = null;
      } else {
        console.log('✅ AuthService: Successfully authenticated');
      }
      
      return {
        authenticated: data.authenticated,
        user: data.user
      };
    } catch (error) {
      console.error('Error checking auth status:', error);
      return { authenticated: false, user: null };
    }
  }

  /**
   * Get current session
   */
  async getSession(): Promise<AuthSession | null> {
    if (!this.sessionId) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/session/${this.sessionId}`);
      const data = await response.json();
      
      if (data.success) {
        return data.session;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      if (this.sessionId) {
        await fetch(`${this.baseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ sessionId: this.sessionId })
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear local storage and session
      localStorage.removeItem('auth_session_id');
      this.sessionId = null;
      this.stopPolling();
      this.closePopup();
      
      // Trigger logout event
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    const status = await this.checkAuthStatus();
    return status.user;
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const status = await this.checkAuthStatus();
    return status.authenticated;
  }
}

// Export singleton instance
export const authService = new AuthService();
